<script setup lang="ts">
import { computed, ref } from 'vue'
import useTithelyNavSettings from '@/composables/nav/trinitySettings'
import { cva } from 'class-variance-authority'
import { PhCaretDown, PhGearSix, PhUser } from '@phosphor-icons/vue'
import TrinityNavIconLink from '@/layouts/components/TrinityNavIconLink.vue'
import { isEmpty } from 'lodash'

type NavProp = `user` | `church`

const props = defineProps<{
    nav: NavProp
}>()

const { filteredNav } = useTithelyNavSettings()

const open = ref(false)

const navItems = computed(() => {
    switch (props.nav) {
        case `user`:
            return [...filteredNav(`userAndOrganization`), ...filteredNav(`account`)]
        case `church`:
            return filteredNav(`church`)
    }
    return []
})

const navHeaderText = computed(() => {
    const orgNavEmpty = isEmpty(filteredNav(`userAndOrganization`))
    switch (props.nav) {
        case `user`:
            return orgNavEmpty ? `nav.myUser` : `nav.mobileOrganizationSettings`
        case `church`:
            return `nav.churchManagement`
    }
    return ``
})

const buttonClasses = cva(`flex w-full items-center gap-2 !px-4 !py-3 text-base/normal transition-all`, {
    variants: {
        open: {
            true: `bg-primary/20 font-semibold text-primary`,
            false: `font-medium`
        }
    }
})

const caretClasses = cva([`!ml-1`, `size-4`, `transition-transform`, `duration-200`, `ease-in-out`], {
    variants: {
        open: {
            true: `rotate-180`,
            false: `rotate-0`
        }
    }
})
</script>

<template>
    <button
        type="button"
        :class="buttonClasses({ open })"
        @click="open = !open">
        <div class="flex w-8 shrink-0 grow-0 items-center justify-center">
            <PhGearSix
                v-if="nav === `church`"
                weight="duotone"
                class="size-6" />
            <PhUser
                v-else
                weight="duotone"
                class="size-6" />
        </div>
        {{ $t(navHeaderText) }}
        <div class="ml-auto flex w-12 shrink-0 grow-0 items-center justify-center">
            <PhCaretDown
                weight="bold"
                :class="caretClasses({ open })" />
        </div>
    </button>
    <ul v-if="open">
        <li
            v-for="(link, index) in navItems"
            :key="`Nav${index}`">
            <TrinityNavIconLink
                v-bind="link"
                mobile />
        </li>
    </ul>
</template>
